"use client";

import { extractLinkFromActions } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/__internal__/ActionHandler/extractLinkFromActions";
import { useSDUIOnClickHandler } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/__internal__/ActionHandler/useSDUIOnClickHandler";
import {
  LoggingEventParams,
  SDUIComponentInstanceRendererProps,
} from "@faire/deprecated-retailer-visitor-shared/components/SDUI/__internal__/types";
import { getSlateButtonVariant } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/ComponentRenderers/Button/getSlateButtonVariant";
import {
  assignInlineVars,
  DynamicVariable,
} from "@faire/deprecated-retailer-visitor-shared/lib/tailwindUtils";
import { getColor } from "@faire/sdui/lib/getColor";
import { getIcon } from "@faire/sdui/lib/getIcon";
import { IButtonElement } from "@faire/web-api--source/indigofair/data/IButtonElement";
import { IconButton } from "@faire/web/slate-deprecated/IconButton";
import { Button } from "@faire/web/slate/Button/Button";
import { ResponsiveProp } from "@faire/web/slate/Theme/ResponsiveProp";
import { cn } from "@faire/web/tools/cn";
import { StandardPropertiesHyphen } from "csstype";
import React from "react";

const varColor: DynamicVariable<"--f-sdui-button-color"> = {
  variable: "--f-sdui-button-color",
  className: "text-(--f-sdui-button-color)!",
};

const varBackgroundColor: DynamicVariable<"--f-sdui-button-bg"> = {
  variable: "--f-sdui-button-bg",
  className: "bg-(--f-sdui-button-bg)!",
};

const varColorDivChild: DynamicVariable<"--f-sdui-button-color"> = {
  variable: "--f-sdui-button-color",
  className: "[&>div]:text-(--f-sdui-button-color)",
};

const varBackgroundColorDivChild: DynamicVariable<"--f-sdui-button-bg"> = {
  variable: "--f-sdui-button-bg",
  className: "[&>div]:bg-(--f-sdui-button-bg)",
};

interface IProps {
  buttonElement: IButtonElement;
  loggingData: LoggingEventParams;
  className?: string;
  // TODO: https://fairewholesale.atlassian.net/browse/FD-217518
  // fireLegacyOnClickEvent should be removed once banners and grids are fully migrated to SDUI
  // and data pipelines and dashboards no longer need the layout element events
  fireLegacyOnClickEvent?: boolean;
  onClickClientDismissHandler?: () => void;
  width?: ResponsiveProp<StandardPropertiesHyphen["width"]>;
  minWidth?: ResponsiveProp<StandardPropertiesHyphen["min-width"]>;
}

export const SDUIButtonRendererWrapper: React.FC<
  SDUIComponentInstanceRendererProps
> = ({ component, loggingData, onClickClientDismissHandler }) => {
  if (!component.button_component?.button_element) {
    return null;
  }
  return (
    <SDUIButtonRenderer
      buttonElement={component.button_component.button_element}
      loggingData={loggingData}
      onClickClientDismissHandler={onClickClientDismissHandler}
      minWidth="auto"
    />
  );
};

export const SDUIButtonRenderer: React.FC<IProps> = ({
  buttonElement,
  loggingData,
  className,
  fireLegacyOnClickEvent,
  onClickClientDismissHandler,
  minWidth = "auto",
  width,
}) => {
  const { icon, text, icon_and_text, variant, on_click, style_override } =
    buttonElement;
  const { link, newTabLink, nonNavigationActions } = extractLinkFromActions(
    on_click ?? []
  );
  const onClickHandler = useSDUIOnClickHandler(
    nonNavigationActions,
    loggingData,
    { logSDUIClickEvent: true, logLegacyClickEvent: fireLegacyOnClickEvent }
  );
  const onClickWithLinkHandler = useSDUIOnClickHandler(on_click, loggingData, {
    logSDUIClickEvent: true,
    logLegacyClickEvent: fireLegacyOnClickEvent,
  });

  const hasConductorDismissAction = nonNavigationActions.some(
    (action) => action.dismiss?.conductor_component_token
  );

  if (icon) {
    const Icon = getIcon(icon);
    const backgroundColor = style_override?.background_color;
    const color = icon.color_token ?? style_override?.color;
    return (
      <IconButton
        data-test-id="sdui-button-renderer-icon-button"
        variant={getSlateButtonVariant(variant)}
        onClick={(e: React.MouseEvent<HTMLElement>) => {
          if (hasConductorDismissAction) {
            onClickClientDismissHandler?.();
          }
          onClickWithLinkHandler(e);
        }}
        aria-label={text ?? ""}
        className={cn(
          backgroundColor || color
            ? varBackgroundColorDivChild.className +
                " " +
                varColorDivChild.className
            : undefined,
          className
        )}
        style={assignInlineVars({
          [varBackgroundColorDivChild.variable]: getColor(backgroundColor),
          [varColorDivChild.variable]: getColor(color),
        })}
      >
        <Icon color={getColor(icon.color_token)} />
      </IconButton>
    );
  }

  const IconComponent = getIcon(icon_and_text?.icon);
  const buttonText = icon_and_text?.text ?? text;

  const flexDirectionClassName =
    getIconPositionFlexDirection(icon_and_text?.icon_position) === "row-reverse"
      ? "flex-row-reverse"
      : "flex-row";

  return (
    <Button
      variant={getSlateButtonVariant(variant)}
      linkTo={newTabLink ?? link}
      target={newTabLink ? "_blank" : undefined}
      onClick={(e: React.MouseEvent<HTMLElement>) => {
        if (hasConductorDismissAction) {
          onClickClientDismissHandler?.();
        }
        onClickHandler(e);
      }}
      className={cn(
        style_override
          ? varBackgroundColor.className + " " + varColor.className
          : undefined,
        icon_and_text?.icon
          ? "flex gap-x-2 " + flexDirectionClassName
          : undefined,
        className
      )}
      style={assignInlineVars({
        [varBackgroundColor.variable]: getColor(
          style_override?.background_color
        ),
        [varColor.variable]: getColor(style_override?.color),
      })}
      minWidth={minWidth}
      width={width}
    >
      {icon_and_text?.icon ? (
        <IconComponent color={getColor(icon_and_text?.icon?.color_token)} />
      ) : null}
      {buttonText}
    </Button>
  );
};

const getIconPositionFlexDirection = (
  icon_position:
    | keyof typeof IButtonElement.IFaireIconText.FaireIconPosition
    | undefined
) => {
  switch (icon_position) {
    case IButtonElement.IFaireIconText.FaireIconPosition.END:
      return "row-reverse";
    case IButtonElement.IFaireIconText.FaireIconPosition.START:
    default:
      return "row";
  }
};
